import clsx from 'clsx'
import moment from 'moment'
import { rem } from 'polished'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import FlightItemCardSkeleton from 'tripPlanner/components/ItemCard/Flight/FlightItemCardSkeleton'
import type { TripContextValue } from 'tripPlanner/contexts/TripContext'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useAirport } from 'tripPlanner/hooks/api/flights'
import type { FlightSchedule } from 'tripPlanner/types/common'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import NestableClickable from 'components/Common/NestableClickable'
import FlightItemCardContent from 'tripPlanner/components/ItemCard/Flight/FlightItemCardContent'

interface Props {
  flight: FlightSchedule
  showSelect?: boolean
  selected?: boolean
  onClick: (flightKey: string) => void
}

const Card = styled(NestableClickable)`
  display: grid;
  border-radius: ${(props) => props.theme.borderRadius.M};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(16)};
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  break-inside: avoid-page;
  transition: border-color 0.2s;

  &.selected {
    border-color: ${props => props.theme.palette.brand.primary.normal};
  }
`

const defaultTripContext: TripContextValue = {
  tripId: '',
  isPublicTrip: false,
  permissions: {
    canAdmin: false,
    canEdit: false,
  },
  name: '',
  displayContext: 'tripPlanner',
}

const WithSelect = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

function FlightLookupTile({
  flight,
  showSelect,
  selected,
  onClick: onClickProp,
}: Props) {
  const { data: departureAirport, isLoading: departureAirportLoading } =
    useAirport({ code: flight.departureAirportCode })

  const { data: arrivalAirport, isLoading: arrivalAirportLoading } = useAirport(
    { code: flight.arrivalAirportCode },
  )

  const onClick = useCallback(
    () => onClickProp(flight.key),
    [onClickProp, flight.key],
  )

  // Take our lookup information and create an item from it to display with
  const flightItem = useMemo(
    (): FlightItem => ({
      isBooked: false,
      type: 'FLIGHT',
      tripId: '',
      confirmationItemCodes: [],
      id: '',
      createdAt: moment(),
      updatedAt: moment(),
      createdBy: '',
      updatedBy: '',
      sourceType: 'NA',
      originAirportCode: departureAirport?.airportCode ?? '',
      destinationAirportCode: arrivalAirport?.airportCode ?? '',
      startPlace: {
        name: departureAirport?.airportName ?? '',
        type: 'CUSTOM',
      },
      endPlace: {
        name: arrivalAirport?.airportName ?? '',
        type: 'CUSTOM',
      },
      startDate: flight.departureDate,
      startTime: flight.departureTime,
      startUtcDateTime: undefined,
      startTimezone: undefined,
      endDate: flight.arrivalDate,
      endTime: flight.arrivalTime,
      endUtcDateTime: undefined,
      endTimezone: undefined,
      airlineName: flight.airlineName,
      flightNumber: `${flight.airlineCode}${flight.flightNumber}`,
      sortableName: departureAirport?.airportName ?? '',
      summaryRankString: 'i',
      itineraryRankString: 'i',
      isSuggested: false,
      isAiGenerated: false,
    }),
    [arrivalAirport, departureAirport, flight],
  )

  if (departureAirportLoading || arrivalAirportLoading) {
    return <FlightItemCardSkeleton />
  }

  if (!arrivalAirport || !departureAirport) {
    return null
  }

  return (
    <WithSelect>
      {showSelect && (
        <CircleCheckboxInput onClick={onClick} checked={!!selected} />
      )}
      <TripContext.Provider value={defaultTripContext}>
        <Card className={clsx({ selected: showSelect && !!selected })} onClick={showSelect ? onClick : undefined}>
          <FlightItemCardContent item={flightItem} variant="tripSummary" />
        </Card>
      </TripContext.Provider>
    </WithSelect>
  )
}

export default memo(FlightLookupTile)
